import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <Tooltip title={copied ? "Скопійовано!" : "Копіювати"}>
            <Button
                size="small"
                variant="outlined"
                startIcon={copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
                color={copied ? "success" : "primary"}
            >
                {copied ? "Скопійовано" : "Копіювати"}
            </Button>
        </Tooltip>
    );
}

function CodeBlock({ children }) {
    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: "grey.50", fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all", overflowX: "auto" }}
        >
            {children}
        </Paper>
    );
}

export default function AbankPage() {
    const [publicKey, setPublicKey] = useState(null);
    const [keyLoading, setKeyLoading] = useState(true);
    const [keyError, setKeyError] = useState("");

    const [systemId, setSystemId] = useState(() => localStorage.getItem("abank_system_id") || "");
    const [systemIdSaved, setSystemIdSaved] = useState(!!localStorage.getItem("abank_system_id"));

    useEffect(() => {
        axios.get("/api/abank/public-key")
            .then(({ data }) => setPublicKey(data.publicKey))
            .catch(e => setKeyError(e?.response?.data?.error || "Не вдалося завантажити публічний ключ"))
            .finally(() => setKeyLoading(false));
    }, []);

    const saveSystemId = () => {
        localStorage.setItem("abank_system_id", systemId.trim());
        setSystemIdSaved(true);
    };

    const activeStep = !publicKey ? 0 : !systemIdSaved ? 1 : 2;

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>А-Банк — Підключення API</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Бізнес API з RSA-підписом · msb@a-bank.com.ua
                    </Typography>
                </Box>

                <Stepper activeStep={activeStep} orientation="vertical">

                    {/* КРОК 1: Публічний ключ */}
                    <Step expanded completed={!!publicKey}>
                        <StepLabel>
                            <Typography fontWeight={600}>Крок 1 — Публічний ключ (publickey.pem)</Typography>
                        </StepLabel>
                        <StepContent>
                            <Stack spacing={2} sx={{ pt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    RSA 2048 ключова пара вже згенерована на сервері.
                                    Приватний ключ зберігається у <code>abank_privatekey.pem</code> і ніколи не покидає сервер.
                                    Публічний ключ потрібно надіслати на <strong>msb@a-bank.com.ua</strong>.
                                </Typography>

                                {keyLoading && <CircularProgress size={24} />}
                                {keyError && <Alert severity="error">{keyError}</Alert>}

                                {publicKey && (
                                    <>
                                        <CodeBlock>{publicKey}</CodeBlock>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <CopyButton text={publicKey} />
                                            <Typography variant="caption" color="text.secondary">
                                                Скопіюйте і надішліть цей ключ на msb@a-bank.com.ua
                                            </Typography>
                                        </Stack>
                                        <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                                            Ключ згенеровано і збережено на сервері.
                                        </Alert>
                                    </>
                                )}
                            </Stack>
                        </StepContent>
                    </Step>

                    {/* КРОК 2: Отримання System ID */}
                    <Step expanded={activeStep >= 1} completed={systemIdSaved}>
                        <StepLabel>
                            <Typography fontWeight={600}>Крок 2 — Ідентифікатор системи (system)</Typography>
                        </StepLabel>
                        <StepContent>
                            <Stack spacing={2} sx={{ pt: 1 }}>
                                <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                                    Надішліть публічний ключ на <strong>msb@a-bank.com.ua</strong> і дочекайтесь відповіді.
                                    Банк надішле вам <strong>system identifier</strong> — вставте його нижче.
                                </Alert>

                                {systemIdSaved ? (
                                    <Stack spacing={1}>
                                        <Alert severity="success" icon={false}>
                                            System ID збережено: <strong>{systemId}</strong>
                                        </Alert>
                                        <Box>
                                            <Button size="small" onClick={() => setSystemIdSaved(false)}>
                                                Змінити
                                            </Button>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <TextField
                                            label="System ID від А-Банку"
                                            placeholder="Вставте ідентифікатор із листа від банку"
                                            value={systemId}
                                            onChange={e => setSystemId(e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={saveSystemId}
                                            disabled={!systemId.trim()}
                                            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                        >
                                            Зберегти
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </StepContent>
                    </Step>

                    {/* КРОК 3: Формування підпису та токена */}
                    <Step expanded={activeStep >= 2} completed={false}>
                        <StepLabel>
                            <Typography fontWeight={600}>Крок 3 — Підпис (hash) та токен (deeplink)</Typography>
                        </StepLabel>
                        <StepContent>
                            <Stack spacing={2} sx={{ pt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Тіло запиту підписується приватним ключем (SHA1withRSA → HEX).
                                    Підпис передається в заголовку <code>signature</code>, system ID — в заголовку <code>system</code>.
                                </Typography>
                                <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                                    Цей крок буде доступний після отримання <strong>system ID</strong> від банку (Крок 2).
                                </Alert>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                        Приклад заголовків запиту:
                                    </Typography>
                                    <CodeBlock>
{`signature: <SHA1withRSA підпис тіла запиту в HEX>
system:    ${systemId || "<твій system ID>"}`}
                                    </CodeBlock>
                                </Box>
                            </Stack>
                        </StepContent>
                    </Step>

                    {/* КРОК 4: Активація токена */}
                    <Step expanded={activeStep >= 2}>
                        <StepLabel>
                            <Typography fontWeight={600}>Крок 4 — Активація токена</Typography>
                        </StepLabel>
                        <StepContent>
                            <Stack spacing={1} sx={{ pt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Після отримання deeplink-токена надішліть його разом із списком компаній
                                    (ЄДРПОУ + назва) на <strong>msb@a-bank.com.ua</strong> для активації.
                                    Кожен токен прив'язується до однієї компанії.
                                </Typography>
                                <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                                    Доступно після Кроку 3.
                                </Alert>
                            </Stack>
                        </StepContent>
                    </Step>

                    {/* КРОК 5: Використання API */}
                    <Step expanded={activeStep >= 2}>
                        <StepLabel>
                            <Typography fontWeight={600}>Крок 5 — Дані рахунків</Typography>
                        </StepLabel>
                        <StepContent>
                            <Stack spacing={1} sx={{ pt: 1 }}>
                                <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                                    Буде доступно після активації токена банком (Крок 4).
                                </Alert>
                            </Stack>
                        </StepContent>
                    </Step>

                </Stepper>

                <Divider />

                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Шаблон листа для msb@a-bank.com.ua
                    </Typography>
                    <CodeBlock>
{`Тема: Підключення до Open API А-Банку

Доброго дня,

Просимо підключити нашу систему до Open API А-Банку.
Публічний ключ (publickey.pem) додаємо до листа / вставляємо нижче:

${publicKey || "[ вставте публічний ключ після завантаження сторінки ]"}

Також просимо надати ідентифікатор системи (system identifier)
для подальшого формування запитів.

З повагою`}
                    </CodeBlock>
                    {publicKey && (
                        <Box mt={1}>
                            <CopyButton text={`Тема: Підключення до Open API А-Банку\n\nДоброго дня,\n\nПросимо підключити нашу систему до Open API А-Банку.\nПублічний ключ (publickey.pem) додаємо до листа / вставляємо нижче:\n\n${publicKey}\n\nТакож просимо надати ідентифікатор системи (system identifier)\nдля подальшого формування запитів.\n\nЗ повагою`} />
                        </Box>
                    )}
                </Box>

            </Stack>
        </Container>
    );
}
